/**
 * The directory a field officer carries: every stakeholder and organization
 * list they have opened, and every record they have looked at, kept in
 * IndexedDB so the same page opens with no connection.
 *
 * Runs in both the page and the service worker — no `window` in here.
 */

import {
  STORE_LISTS,
  STORE_RECORDS,
  idbDelete,
  idbGet,
  idbGetAll,
  idbPut,
  type StoreName,
} from './db';
import type { CachedList, CachedRecord } from './types';

/** Caps chosen for a mid-range phone: a few thousand people is plenty. */
export const MAX_RECORDS = 500;
export const MAX_RECORD_BYTES = 6_000_000;
export const MAX_LISTS = 150;
export const MAX_LIST_BYTES = 4_000_000;

/**
 * `stakeholder` and `organization` are the directory itself — those get the
 * visible "stored copy" marker. `app` is everything else the app reads to be
 * usable at all: who is signed in, the taxonomies, the media and wiki lists
 * hanging off a record. Without them a phone with no connection lands on the
 * setup screen instead of the directory it came to show.
 */
export type CacheKind = 'stakeholder' | 'organization' | 'app';

/** Never keep a copy of these: they are one-shot or huge. */
const NEVER_CACHE = [
  /^\/api\/auth\/(login|register|create-superuser|refresh|logout)/,
  /^\/api\/export/,
  /^\/api\/backup/,
  /^\/api\/media\/[^/]+\/(file|download)/,
];

export const recordKey = (kind: CacheKind, id: string): string => `${kind}:${id}`;

const sizeOf = (value: unknown): number => {
  try {
    return JSON.stringify(value)?.length ?? 0;
  } catch {
    return 0;
  }
};

/** Store one record. Overwrites an older copy and keeps the caps. */
export async function putRecord(
  kind: CacheKind,
  id: string,
  data: Record<string, unknown>,
  userId?: string,
  now: number = Date.now(),
): Promise<void> {
  const entry: CachedRecord = {
    key: recordKey(kind, id),
    kind,
    id,
    data,
    cachedAt: now,
    lastAccessAt: now,
    bytes: sizeOf(data),
    userId,
  };
  await idbPut(STORE_RECORDS, entry);
  await evictRecords();
}

/** Read a stored record, marking it as used so it survives eviction longest. */
export async function getRecord(
  kind: CacheKind,
  id: string,
  now: number = Date.now(),
): Promise<CachedRecord | undefined> {
  const entry = await idbGet<CachedRecord>(STORE_RECORDS, recordKey(kind, id));
  if (!entry) return undefined;
  await idbPut(STORE_RECORDS, { ...entry, lastAccessAt: now });
  return entry;
}

export async function putList(
  kind: CacheKind,
  url: string,
  data: unknown,
  userId?: string,
  now: number = Date.now(),
): Promise<void> {
  const entry: CachedList = {
    key: `${kind}:${url}`,
    kind,
    url,
    data,
    cachedAt: now,
    lastAccessAt: now,
    bytes: sizeOf(data),
    userId,
  };
  await idbPut(STORE_LISTS, entry);
  await evictLists();
}

export async function getList(
  kind: CacheKind,
  url: string,
  now: number = Date.now(),
): Promise<CachedList | undefined> {
  const entry = await idbGet<CachedList>(STORE_LISTS, `${kind}:${url}`);
  if (!entry) return undefined;
  await idbPut(STORE_LISTS, { ...entry, lastAccessAt: now });
  return entry;
}

/** Everything saved, newest read first — what the "Saved" tab lists. */
export async function listRecords(kind?: CacheKind): Promise<CachedRecord[]> {
  const all = await idbGetAll<CachedRecord>(STORE_RECORDS);
  return all
    .filter((entry) => !kind || entry.kind === kind)
    .sort((a, b) => b.lastAccessAt - a.lastAccessAt);
}

export interface CacheStats {
  records: number;
  lists: number;
  bytes: number;
  oldestCachedAt?: number;
}

export async function cacheStats(): Promise<CacheStats> {
  const [records, lists] = await Promise.all([
    idbGetAll<CachedRecord>(STORE_RECORDS),
    idbGetAll<CachedList>(STORE_LISTS),
  ]);
  const bytes =
    records.reduce((sum, entry) => sum + (entry.bytes || 0), 0) +
    lists.reduce((sum, entry) => sum + (entry.bytes || 0), 0);
  const oldest = records.reduce<number | undefined>(
    (min, entry) => (min === undefined || entry.cachedAt < min ? entry.cachedAt : min),
    undefined,
  );
  return { records: records.length, lists: lists.length, bytes, oldestCachedAt: oldest };
}

/** Least-recently-read entries go first, by count and by size. */
export async function evictRecords(): Promise<number> {
  const all = await idbGetAll<CachedRecord>(STORE_RECORDS);
  let bytes = all.reduce((sum, entry) => sum + (entry.bytes || 0), 0);
  const ordered = [...all].sort((a, b) => a.lastAccessAt - b.lastAccessAt);
  let removed = 0;
  let count = all.length;

  for (const entry of ordered) {
    if (count <= MAX_RECORDS && bytes <= MAX_RECORD_BYTES) break;
    await idbDelete(STORE_RECORDS, entry.key);
    bytes -= entry.bytes || 0;
    count -= 1;
    removed += 1;
  }
  return removed;
}

export async function evictLists(): Promise<number> {
  const all = await idbGetAll<CachedList>(STORE_LISTS);
  let bytes = all.reduce((sum, entry) => sum + (entry.bytes || 0), 0);
  let count = all.length;
  const ordered = [...all].sort((a, b) => a.lastAccessAt - b.lastAccessAt);
  let removed = 0;

  for (const entry of ordered) {
    if (count <= MAX_LISTS && bytes <= MAX_LIST_BYTES) break;
    await idbDelete(STORE_LISTS, entry.key);
    bytes -= entry.bytes || 0;
    count -= 1;
    removed += 1;
  }
  return removed;
}

/**
 * Shared phones are normal here: when a different officer signs in, the
 * previous one's directory must not be readable from the cache.
 */
export async function dropOtherUsers(userId: string | undefined): Promise<number> {
  const stores: StoreName[] = [STORE_RECORDS, STORE_LISTS];
  let removed = 0;
  for (const store of stores) {
    const all = await idbGetAll<{ key: string; userId?: string }>(store);
    for (const entry of all) {
      if (entry.userId && entry.userId !== userId) {
        await idbDelete(store, entry.key);
        removed += 1;
      }
    }
  }
  return removed;
}

/** Wipe the read cache. The outbox is deliberately left alone. */
export async function clearCache(): Promise<void> {
  const stores: StoreName[] = [STORE_RECORDS, STORE_LISTS];
  for (const store of stores) {
    const all = await idbGetAll<{ key: string }>(store);
    for (const entry of all) {
      await idbDelete(store, entry.key);
    }
  }
}

/**
 * What a GET is, as far as the cache is concerned:
 * a directory record, a directory list, or another API read worth keeping so
 * the app still works. Anything one-shot or enormous is left alone.
 */
export function classifyApiUrl(
  url: string,
): { kind: CacheKind; id?: string } | undefined {
  let path = url;
  try {
    path = new URL(url, 'http://local').pathname;
  } catch {
    /* already a path */
  }
  if (!path.startsWith('/api/')) return undefined;
  if (NEVER_CACHE.some((pattern) => pattern.test(path))) return undefined;

  const match = /^\/api\/(stakeholders|organizations)(?:\/([^/]+))?\/?$/.exec(path);
  if (!match) return { kind: 'app' };

  const kind: CacheKind = match[1] === 'stakeholders' ? 'stakeholder' : 'organization';
  const id = match[2];
  // `/api/stakeholders/merge` and friends are actions, not records.
  if (id && !/^[0-9a-f-]{8,}$/i.test(id)) return { kind: 'app' };
  return { kind, id };
}
